import { useEffect, useRef } from 'react';

interface HeroSectionProps {
  onParticipate: () => void;
  onSponsor: () => void;
}

export default function HeroSection({ onParticipate, onSponsor }: HeroSectionProps) {
  const sectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.querySelectorAll('.animate-target').forEach((el, i) => {
              (el as HTMLElement).style.animationDelay = `${i * 0.18}s`;
              el.classList.add('animate-fade-up');
              (el as HTMLElement).style.opacity = '1';
            });
          }
        });
      },
      { threshold: 0.1 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section
      ref={sectionRef}
      className="relative min-h-screen flex flex-col overflow-hidden bg-obsidian"
      style={{ fontFamily: 'Montserrat, sans-serif' }}
    >
      {/* Background image */}
      <div className="absolute inset-0 z-0">
        <img
          src="https://storage.readdy-site.link/project_files/95ab1d24-f5e3-49af-acf5-f9d2f5689cc7/b32b8ae9-f392-4781-b19c-943206fb7381_Purple-Gradient-Modern-Event-Planner-Marketing-Presentation.png?v=d76e01f08f5cd0dc003b69dacf462388"
          alt="O Rugido das Leoas"
          className="w-full h-full object-cover object-top"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-obsidian/40 via-obsidian/20 to-obsidian/70" />
      </div>

      {/* Gold top line */}
      <div className="absolute top-0 left-0 right-0 h-px bg-gold/40 z-10" />

      {/* ── ZONA SUPERIOR: Logo centralizado ── */}
      <div className="relative z-20 flex justify-center items-center pt-20 md:pt-24 pb-4 md:pb-6">
        <div className="animate-target opacity-0">
          <img
            src="https://static.readdy.ai/image/61892072331720d5ae52870a50f3ccd8/025c391ac7e8948bae59369a022c826a.png"
            alt="O Rugido das Leoas"
            className="h-40 sm:h-52 md:h-64 lg:h-72 w-auto object-contain drop-shadow-2xl"
          />
        </div>
      </div>

      {/* ── ZONA INFERIOR: Conteúdo ── */}
      <div className="relative z-10 flex-1 flex flex-col items-center justify-center text-center px-6 pb-16 md:pb-20">

        {/* Date tag */}
        <div className="animate-target opacity-0 mb-5 md:mb-7">
          <span
            className="font-sans text-xs uppercase text-gold border border-gold/40 px-4 md:px-6 py-2 inline-block"
            style={{ letterSpacing: '0.25em' }}
          >
            AGENDA CONFIRMADA PARA SP &nbsp;|&nbsp; 15/08 · EM Alphaville
          </span>
        </div>

        {/* Gold thin line */}
        <div className="animate-target opacity-0 w-14 h-px bg-gold mx-auto mb-6 md:mb-8" />

        {/* Main title */}
        <div className="animate-target opacity-0 mb-1">
          <h1 className="font-serif text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-bold text-cream leading-none tracking-tight">
            O Rugido
          </h1>
        </div>
        <div className="animate-target opacity-0 mb-1">
          <span className="font-serif text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-light italic text-gold leading-none">
            das
          </span>
        </div>
        <div className="animate-target opacity-0 mb-7 md:mb-9">
          <span className="font-serif text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-bold text-cream leading-none tracking-tight">
            LEOAS
          </span>
        </div>

        {/* Gold line */}
        <div className="animate-target opacity-0 w-20 h-px bg-gold mx-auto mb-5 md:mb-7" />

        {/* Subtitle */}
        <p
          className="animate-target opacity-0 font-sans text-xs md:text-sm uppercase text-cream/60 mb-6 leading-relaxed"
          style={{ letterSpacing: '0.15em' }}
        >
          Mulheres que constroem
          <span className="hidden sm:inline">&nbsp;·&nbsp;</span>
          <span className="block sm:hidden" />
          Mulheres que lideram
          <span className="hidden sm:inline">&nbsp;·&nbsp;</span>
          <span className="block sm:hidden" />
          Mulheres que rugem
        </p>

        {/* Italic phrase */}
        <p className="animate-target opacity-0 font-serif italic text-sm md:text-base text-gold/70 max-w-xl mx-auto mb-10 md:mb-12 leading-relaxed px-2">
          &ldquo;Na savana, o leão ruge para intimidar. A leoa ruge para alimentar. Nós rugimos para construir.&rdquo;
        </p>

        {/* CTAs */}
        <div className="animate-target opacity-0 flex flex-col sm:flex-row gap-4 justify-center items-center w-full max-w-md sm:max-w-none mx-auto">
          <button
            onClick={onParticipate}
            className="w-full sm:w-auto font-sans text-xs uppercase bg-gold text-obsidian px-8 md:px-10 py-4 hover:bg-gold-dark transition-all duration-300 cursor-pointer whitespace-nowrap font-semibold"
            style={{ letterSpacing: '0.2em' }}
          >
            Garanta Sua Presença
          </button>
          <button
            onClick={onSponsor}
            className="w-full sm:w-auto font-sans text-xs uppercase border border-gold/60 text-gold px-8 md:px-10 py-4 hover:border-gold hover:bg-gold/10 transition-all duration-300 cursor-pointer whitespace-nowrap"
            style={{ letterSpacing: '0.2em' }}
          >
            Oportunidades de Patrocínio
          </button>
        </div>

        {/* Scroll indicator */}
        <div className="animate-target opacity-0 mt-10 md:mt-14 flex flex-col items-center gap-2">
          <span className="font-sans text-xs text-cream/30 uppercase" style={{ letterSpacing: '0.3em' }}>scroll</span>
          <div className="w-px h-8 bg-gradient-to-b from-gold/40 to-transparent" />
        </div>
      </div>

      {/* Gold bottom line */}
      <div className="absolute bottom-0 left-0 right-0 h-px bg-gold/30 z-10" />
    </section>
  );
}
